import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dialog } from '../../../../../Dialog/Dialog';
import { Button } from '../../../../../Button/Button';
import {
  OnQuickReportsChanged,
  QueryModel,
  SortModel
} from '../../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { Typography } from '../../../../../Typography/Typography';
import { useDataTableContext } from '../../../../utils/DataTableContext';
import {
  FileFormat,
  CreateScheduleExport
} from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import { useIntlUds } from '../../../../../../translations/UdsIntlProvider';
import { filterNotEmpty } from '../../../../../../utils/listHelpers';
import { convertBEScheduleToUDS } from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.utils';
import { ScheduleExportForm } from './ScheduleExportForm';
import { useScheduleExportForm } from './useScheduleExportForm';

interface Props<Q extends QueryModel, S extends SortModel> {
  report: OnQuickReportsChanged<Q, S> | undefined;
  exportFileFormats: FileFormat[];
  onClose: () => void;
  onExportDelete: () => void;
  onSave: (createScheduleExport: CreateScheduleExport, report: OnQuickReportsChanged<Q, S>) => Promise<boolean>;
  children?: React.ReactNode;
}

export const ScheduleExportDialog = <Q extends QueryModel, S extends SortModel>({
  report,
  onClose,
  onSave,
  onExportDelete,
  exportFileFormats,
  children
}: Props<Q, S>) => {
  const { intl } = useIntlUds();
  const isEdit = !!report?.schedule?.uid;
  const [error, setError] = useState<boolean>(false);

  const { getQueryData, scheduleExport, setScheduleExport } = useScheduleExportForm(
    convertBEScheduleToUDS(report?.schedule)
  );
  const { isFetching } = useDataTableContext();

  useEffect(() => {
    if (scheduleExport.startTimestamp) {
      setError(false);
    }
  }, [scheduleExport.startTimestamp]);

  const handleOnSave = useCallback(async () => {
    if (!report) return;

    const { startTimestamp, fileFormat, timeInterval } = getQueryData();

    if (!scheduleExport.startTimestamp) {
      setError(true);
      return;
    }

    const response = await onSave?.(
      {
        startTimestamp,
        fileFormat,
        timeInterval,
        quickReportUid: report.uid ?? '',
        uid: report.schedule?.uid
      },
      report
    );
    if (response) {
      onClose();
    }
  }, [getQueryData, onClose, onSave, report, scheduleExport.startTimestamp]);

  return (
    <Dialog
      dataTest="schedule-export-dialog"
      title={intl.DataTable.ScheduleExport.CreateDialog.title}
      open={!!report}
      onClose={onClose}
      showCancelButton
      actions={filterNotEmpty([
        isEdit && (
          <Button
            loading={isFetching}
            key="remove-schedule-submit-button"
            dataTest="remove-schedule"
            variant="text"
            error
            onClick={onExportDelete}
          >
            {intl.DataTable.ScheduleExport.EditDialog.deleteBtn}
          </Button>
        ),

        <Button
          key="schedule-export-submit-button"
          dataTest="submit"
          variant="contained"
          color="primary"
          loading={isFetching}
          onClick={handleOnSave}
        >
          {isEdit ? intl.DataTable.ScheduleExport.EditDialog.btn : intl.DataTable.ScheduleExport.CreateDialog.btn}
        </Button>
      ])}
    >
      <StyledDialogWrapper>
        <Typography>
          {intl.DataTable.ScheduleExport.CreateDialog.message}
          <br />
          <br />
          <b>{report?.name}</b>
        </Typography>
        <ScheduleExportForm
          formProps={{
            datePicker: {
              errorMessage: error ? intl.DataTable.ScheduleExport.Form.startDateInput.validation.required : ''
            }
          }}
          scheduleExport={scheduleExport}
          setScheduleExport={setScheduleExport}
          exportFileFormats={exportFileFormats}
        />
        {children}
      </StyledDialogWrapper>
    </Dialog>
  );
};

const StyledDialogWrapper = styled.div`
  .MuiAutocomplete-popupIndicatorOpen {
    transform: rotate(0);
  }
`;
